import { action, observable, toJS } from 'mobx';

import { EAjaxFunctions, EResponseStatus } from '@enums';

import { Log, shuffleArray } from '@utils';

import { getCurrentUser } from '@stores';

import { Api } from '@api';

import { getAdaptedApps, getAdaptedSales } from './utils';

import { IAppData, IAppsData } from '@interfaces';
import { ICGIUrlOptions } from '@interfaces/utils';

import { IAppsStore } from './types';

const emptyData: IAppsData = {
	apps: [],
	offset: 0,
	hasMore: false,
	total: 0,
};

export class AppsStore {

	constructor(func: EAjaxFunctions) {
		this.func = func;
	}

	func: EAjaxFunctions = null;

	@observable
	status: EResponseStatus = null;

	@observable
	data: IAppsData = emptyData;

	@action
	async fetch(options: ICGIUrlOptions, ids?: string[]) {
		if (!this.data) {
			this.data = emptyData;
		}

		// this.status = EResponseStatus.PENDING;

		const response = await this.fetchData({
			...options,
			appId: ids?.join(',') || null,
		});

		if (options.isShuffle && response?.apps?.length) {
			shuffleArray(response?.apps);
		}
		this.rewriteAppsStore(response);
	};

	@action
	async fetchNext(options: ICGIUrlOptions) {
		const response = await this.fetchData(options);

		this.addToAppsStore(response);
	};

	@action
	clearStore() {
		this.data = emptyData;
		this.status = null;
	}

	@action
	async uninstallApp(appId: string) {
		const user = getCurrentUser();

		await Api.apps.uninstallApp(appId, user);
		this.data.apps = this.data.apps.filter((app: IAppData) => {
			return app.id !== appId;
		});
	}

	@action
	static async sendAppRatingData(appId: string, rating: number) {
		const user = getCurrentUser();
		try {
			await Api.apps.rateApp(
				appId,
				rating,
				user,
			);
		} catch (err) {
			Log.error(`Couldn't rate the app: ${err}`);
		}
	}

	@action
	setRating(appId: string, userRating: number) {
		const app = this.data.apps.filter((app: IAppData) => app.id === appId)[0];

		app.rating = Math.round((app.rating * app.voters + userRating) / (app.voters + 1) * 100) / 100;
		app.voters = app.voters + 1;
	}

	@action
	setStatus(status: EResponseStatus) {
		this.status = status;
	}

	async fetchData(options: ICGIUrlOptions) {
		let response: IAppsData | null = null;

		this.setStatus(EResponseStatus.PENDING);

		try {
			if (this.func === EAjaxFunctions.APPS_SALES) {
				response = getAdaptedSales(await Api.apps.fetchSales(this.func, { target: 'catalog', ...options }));
			} else {
				response = getAdaptedApps(await Api.apps.fetchApps(this.func, { ...options }));
			}
		} catch (err) {
			Log.error(`Failed in getting apps in AppsStore; Error: ${err}`);
		}

		this.status = response ? EResponseStatus.OK : EResponseStatus.ERROR;

		return response;
	};

	addToAppsStore(responseData: IAppsData) {
		const newAppsArray = [...this.data.apps, ...responseData.apps];

		this.data = { ...responseData, apps: newAppsArray };
	}

	rewriteAppsStore(responseData: IAppsData) {
		this.data = responseData;
	}

	toJSON() {
		return {
			data: this.data,
			status: this.status,
		};
	}

	fromJSON(store: IAppsStore) {
		this.data = { ...toJS(this.data), ...store.data };
		this.status = store.status;
	}

}
